import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { showErrorToaster } from '@/utils/toasters';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useAppState } from '@/providers/AppStateProvider';
import type { Answers } from './types';
import {
  buildInitialAnswers,
  fetchForm,
  fetchPracticeForms,
  fetchSubmission,
} from './utils';

export type { Answers };

type FormProviderValue = {
  isChanged: boolean;
  isLoading: boolean;
  answers: Answers;
  formId?: number;
  versionId?: number;
  submissionId?: number;
  activeFieldId?: number;
  onSelectSubmission: (submissionId: number) => void;
  onSelectForm: (formId: number) => void;
  onSelectField: (fieldId: number) => void;
  onUpdateAnswer: (fieldId: number, value: string) => void;
  onSetAnswers: (answers: Answers) => void;
};

export interface FormProviderProps extends PropsWithChildren {
  initialFormId?: number;
  initialSubmissionId?: number;
  initialAnswers?: Answers;
}

const FormContext = createContext<FormProviderValue | undefined>(undefined);

export default function FormProvider({
  initialFormId,
  initialSubmissionId,
  initialAnswers,
  children,
}: FormProviderProps) {
  // NOTE: using a ref to handle stale state merging
  const answersRef = useRef<Answers>(buildInitialAnswers());
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const [formId, setFormId] = useId(initialFormId);
  const [submissionId, setSubmissionId] = useId(initialSubmissionId);
  const [versionId, setVersionId] = useId();
  const [activeFieldId, setActiveFieldId] = useId();
  const [answers, setAnswers] = useState<Answers>(buildInitialAnswers());
  const [isChanged, toggleChanged] = useToggle();
  const [isLoading, toggleLoading] = useToggle(true);

  useEffect(() => {
    if (activePractice) fetchForms();
  }, [activePractice]);

  useEffect(() => {
    if (formId && !submissionId) loadForm();
  }, [formId]);

  useEffect(() => {
    if (submissionId) loadSubmission();
  }, [submissionId]);

  const fetchForms = async () => {
    if (formId) return; // No need to fetch them all if one is selected
    try {
      toggleLoading(true);
      dispatch.addForms(await fetchPracticeForms(activePractice!.id));
      toggleLoading(false);
    } catch (err) {
      toggleLoading(false);
      onApiError(err, 'Error fetching forms', () => fetchForms());
    }
  };

  const loadForm = async () => {
    try {
      toggleLoading(true);
      const data = await fetchForm(formId!);
      if (!data.version) {
        toggleLoading(false);
        return showErrorToaster('Template has no version');
      }
      dispatch.addForms([data.form]);
      dispatch.addFormVersions([data.version]);
      setVersionId(data.version.id);
      handleStoreUpdates(initialAnswers ?? data.answers);
      toggleLoading(false);
    } catch (err) {
      onApiError(err, 'Error loading form', () => loadForm());
    }
  };

  const loadSubmission = async () => {
    try {
      toggleLoading(true);
      // TODO
      const data = await fetchSubmission(submissionId!);
      dispatch.addForms([data.form]);
      dispatch.addFormVersions([data.formVersion]);
      dispatch.addSubmissions([data.formSubmission]);
      setFormId(data.form.id);
      setVersionId(data.formVersion.id);
      handleStoreUpdates(data.answers);
      toggleLoading(false);
    } catch (err) {
      onApiError(err, 'Error loading submission', () => loadForm());
    }
  };

  const handleStoreUpdates = (newAnswers: Answers) => {
    answersRef.current = newAnswers;
    setAnswers(answersRef.current);
  };

  const handleUpdateAnswer = (fieldId: number, answer: string) => {
    handleStoreUpdates({ ...answersRef.current, [fieldId]: answer });
    if (!isChanged) toggleChanged(true);
  };

  const contextValue: FormProviderValue = {
    isChanged,
    isLoading,
    answers,
    formId,
    versionId,
    submissionId,
    activeFieldId,
    onSelectForm: setFormId,
    onSelectSubmission: setSubmissionId,
    onSelectField: setActiveFieldId,
    onUpdateAnswer: handleUpdateAnswer,
    onSetAnswers: handleStoreUpdates,
  };

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
}

export function useFormProvider() {
  const context = useContext(FormContext);
  if (!context) {
    throw Error('useFormProvider must be used within a FormProvider');
  }
  return context;
}
